import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FiHome, FiSearch, FiPhone, FiUser, FiLogIn } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const MobileBottomNav = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();

    // Scroll hide/show state
    const [visible, setVisible] = useState(true);
    const lastScrollY = useRef(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY;
            const scrollDelta = currentY - lastScrollY.current;

            // Show when scrolling up significantly or near top
            if (currentY < 10 || scrollDelta < -10) {
                setVisible(true);
            }
            // Hide when scrolling down significantly
            else if (currentY > 100 && scrollDelta > 10) {
                setVisible(false);
            }
            lastScrollY.current = currentY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Hide on admin routes
    if (location.pathname.startsWith('/admin')) {
        return null;
    }

    const handleProfileClick = (e) => {
        e.preventDefault();
        if (!isAuthenticated) {
            navigate('/login');
        } else {
            if (user.role === 'admin') navigate('/admin-dashboard/dashboard');
            else if (user.role === 'worker') navigate('/worker/dashboard');
            else navigate('/user/dashboard');
        }
    };

    const navItems = [
        { name: 'Home', path: '/', icon: FiHome, exact: true },
        { name: 'Search', path: '/workers?mode=search', icon: FiSearch },
        { name: 'Contact', path: '/contact', icon: FiPhone },
        {
            name: 'Profile',
            path: '/profile-check',
            icon: isAuthenticated ? FiUser : FiLogIn,
            onClick: handleProfileClick,
        }
    ];

    return (
        <nav
            className="fixed bottom-0 left-0 right-0 z-[5000] lg:hidden pb-safe transition-transform duration-300 ease-in-out"
            style={{
                transform: visible ? 'translateY(0)' : 'translateY(100%)',
                willChange: 'transform'
            }}
            role="navigation"
            aria-label="Mobile bottom navigation"
        >
            <div
                className="bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-[0_-4px_16px_rgba(15,23,42,0.08)]"
                style={{ borderRadius: '16px 16px 0 0' }}
            >
                <div className="flex justify-around items-center px-1 py-1.5 max-w-2xl mx-auto">
                    {navItems.map((item) => {
                        const isActive = item.path === '/profile-check'
                            ? (location.pathname.includes('dashboard') || location.pathname === '/login')
                            : (item.exact
                                ? location.pathname === item.path
                                : location.pathname.startsWith(item.path.split('?')[0]));

                        return (
                            <NavLink
                                key={item.name}
                                to={item.path}
                                onClick={item.onClick}
                                className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1 rounded-xl transition-all duration-200"
                                style={{
                                    backgroundColor: isActive ? 'rgba(225, 29, 72, 0.06)' : 'transparent'
                                }}
                            >
                                {/* Icon */}
                                <div className={`
                                    flex items-center justify-center w-7 h-7 rounded-lg
                                    transition-all duration-200
                                    ${isActive ? 'text-rose-600' : 'text-slate-500'}
                                `}>
                                    <item.icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2.5 : 1.5} />
                                </div>

                                {/* Label */}
                                <span className={`
                                    text-[9px] font-semibold tracking-tight transition-all duration-200
                                    ${isActive ? 'text-rose-600' : 'text-slate-400'}
                                `}>
                                    {item.name}
                                </span>
                            </NavLink>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
};

export default MobileBottomNav;
